// File: assets/scroll-to-section.js
// Purpose: Simple, safe smooth scroll to section (anchors only). No history, no popstate.
// Fix: keep mobile tweaks but make them adaptive so deep-links land at section start on mobile.

(function () {
  "use strict";

  const READY = (fn) => {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", fn, { once: true });
    } else {
      fn();
    }
  };

  const normalizePath = (p) => (p || "/").replace(/\/+$/, "") || "/";
  const normalizeAnchor = (a) => (a || "").replace(/^[.#]+/, "");
  const safeDecode = (s) => {
    try {
      return decodeURIComponent(s);
    } catch {
      return s;
    }
  };

  const isModifiedClick = (e) =>
    e.defaultPrevented ||
    e.button !== 0 ||
    e.metaKey ||
    e.ctrlKey ||
    e.shiftKey ||
    e.altKey;

  const MOBILE_MAX_CORRECTION_PX = 200; // keep your "200", but as a *cap*, not a fixed overshoot
  const STILLNESS_MS = 220;
  const ALIGN_EPS_PX = 8;

  function isSamePageLink(a) {
    try {
      const u = new URL(a.href, window.location.href);
      return (
        u.origin === window.location.origin &&
        normalizePath(u.pathname) === normalizePath(window.location.pathname)
      );
    } catch {
      return false;
    }
  }

  function getMaxScrollTop() {
    const doc = document.documentElement;
    return Math.max(0, doc.scrollHeight - window.innerHeight);
  }

  function clampTop(top) {
    const max = getMaxScrollTop();
    if (top < 0) return 0;
    if (top > max) return max;
    return top;
  }

  // --- dynamic offset resolver ---
  function resolveOffset(explicitOffset, targetEl) {
    if (Number.isFinite(explicitOffset)) return explicitOffset;

    const ds = targetEl?.dataset?.scrollOffset;
    if (ds != null && ds !== "") {
      const v = Number(ds);
      if (Number.isFinite(v)) return v;
    }

    const candidates = [targetEl, document.body, document.documentElement].filter(Boolean);
    for (const el of candidates) {
      const raw = getComputedStyle(el).getPropertyValue("--scroll-offset").trim();
      if (raw) {
        const px = Number(raw.replace("px", ""));
        if (Number.isFinite(px)) return px;
      }
    }

    const bodyDS = document.body?.dataset?.scrollOffset;
    if (bodyDS != null) {
      const v = Number(bodyDS);
      if (Number.isFinite(v)) return v;
    }

    return 0;
  }

  // --- target resolver ---
  function findTargetByAnchor(anchor) {
    const key = normalizeAnchor(anchor);
    if (!key) return null;

    let el = null;
    try {
      el = document.querySelector(`.${CSS.escape(key)}`);
      if (!el) el = document.querySelector(`[data-scroll-anchor="${CSS.escape(key)}"]`);
    } catch {
      // ignore invalid selector
    }

    if (!el) el = document.getElementById(key);
    return el || null;
  }

  function waitForScrollStill(cb, timeoutMs = 1600) {
    let timer = null;
    let done = false;

    const finish = () => {
      if (done) return;
      done = true;
      window.removeEventListener("scroll", onScroll);
      clearTimeout(timer);
      cb();
    };

    const onScroll = () => {
      clearTimeout(timer);
      timer = setTimeout(finish, STILLNESS_MS);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    timer = setTimeout(finish, STILLNESS_MS);
    setTimeout(finish, timeoutMs);
  }

  function alignTargetStartMobileExternal(targetEl, offsetPx) {
    if (window.innerWidth > 768) return;

    // After scrolling settles, align target's top to the offset line.
    waitForScrollStill(() => {
      const rectTop = targetEl.getBoundingClientRect().top; // px from viewport top
      const desired = offsetPx; // want target's top to sit here
      const delta = rectTop - desired; // + => target too low (need scroll down), - => too high (need scroll up)

      if (Math.abs(delta) <= ALIGN_EPS_PX) return;

      const correction = Math.max(
        -MOBILE_MAX_CORRECTION_PX,
        Math.min(MOBILE_MAX_CORRECTION_PX, delta)
      );

      // Use auto to avoid re-triggering long smooth scroll chains
      window.scrollBy({ top: correction, behavior: "auto" });
    });
  }

  // --- scroll ---
  function smoothScrollTo(top, behavior = "smooth") {
    try {
      if (top <= 10) return;

      window.__SCROLLING_TO_SECTION__ = true;

      const clamped = clampTop(top);
      window.scrollTo({ top: clamped, behavior });

      setTimeout(() => {
        window.__SCROLLING_TO_SECTION__ = false;
      }, 800);
    } catch {
      window.scrollTo(0, top);
      window.__SCROLLING_TO_SECTION__ = false;
    }
  }

  // --- lightweight scroll (compute geometry at execution time) ---
  function tryScrollToAnchor(anchor, opts = {}) {
    const target = findTargetByAnchor(anchor);
    if (!target) return false;

    const go = () => {
      const off = resolveOffset(opts.offset, target);
      const top = Math.max(0, target.getBoundingClientRect().top + window.scrollY - off);

      smoothScrollTo(top, opts.behavior || "smooth");

      // KEEP your mobile deep-link “tweak”, but make it *adaptive* instead of fixed +200 overshoot.
      if (opts.fromExternal) {
        alignTargetStartMobileExternal(target, off);
      }
    };

    if (opts.delay > 0) setTimeout(go, opts.delay);
    else go();

    return true;
  }

  /**
   * Deep-link stabilization:
   * - only used for fromExternal=true
   * - repeats scroll after layout shifts (images/sections)
   * - does not affect same-page clicks
   */
  function recalibrateAfterScroll(anchor, opts = {}) {
    if (!opts.fromExternal) return;

    const passes = [
      { delay: 0, behavior: "auto" },
      { delay: 32, behavior: "auto" },
      { delay: 180, behavior: "auto" },
      { delay: 520, behavior: "auto" },
      { delay: 900, behavior: "smooth" },
    ];

    passes.forEach((p) => {
      setTimeout(() => {
        tryScrollToAnchor(anchor, {
          ...opts,
          delay: 0,
          behavior: p.behavior,
          fromExternal: true,
        });
      }, p.delay);
    });

    window.addEventListener(
      "load",
      () => {
        tryScrollToAnchor(anchor, { ...opts, delay: 0, behavior: "auto", fromExternal: true });
        setTimeout(() => {
          tryScrollToAnchor(anchor, { ...opts, delay: 0, behavior: "smooth", fromExternal: true });
        }, 180);
      },
      { once: true }
    );
  }

  function ensureScroll(anchor, opts = {}, timeoutMs = 2000) {
    if (tryScrollToAnchor(anchor, opts)) {
      recalibrateAfterScroll(anchor, opts);
      return true;
    }

    const start = performance.now();
    const obs = new MutationObserver(() => {
      if (tryScrollToAnchor(anchor, opts)) {
        obs.disconnect();
        recalibrateAfterScroll(anchor, opts);
      } else if (performance.now() - start > timeoutMs) {
        obs.disconnect();
      }
    });

    obs.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => obs.disconnect(), timeoutMs + 100);
    return false;
  }

  function getInitialAnchor() {
    const u = new URL(window.location.href);
    const initial =
      u.searchParams.get("scrollTo") ||
      (window.location.hash.length > 1 ? safeDecode(window.location.hash.slice(1)) : null);

    return initial ? normalizeAnchor(initial) : null;
  }

  READY(() => {
    if ("scrollRestoration" in history) history.scrollRestoration = "manual";

    // Deep-link on load: stabilize + adaptive alignment on mobile
    const initial = getInitialAnchor();
    if (initial) {
      ensureScroll(initial, { delay: 0, fromExternal: true });
    }

    // Clicks: same-page only (keep working path, no recalibration)
    document.addEventListener("click", (evt) => {
      if (isModifiedClick(evt)) return;

      const inCartArea = !!evt.target.closest(
        [
          'form[action*="/cart"]',
          'form[action*="/cart/add"]',
          "[data-product-form]",
          "product-form",
          "[data-cart-form]",
          ".product-form",
          ".shopify-payment-button",
          "[data-no-scroll]",
        ].join(",")
      );

      const a = evt.target.closest("a[href]");
      if (!a) return;

      if (inCartArea) {
        const href = (a.getAttribute("href") || "").trim();
        if (href === "#" || href === "") evt.preventDefault();
        return;
      }

      if (!isSamePageLink(a)) return;

      let anchor = null;
      try {
        const u = new URL(a.href, window.location.href);
        anchor = u.searchParams.get("scrollTo") || (u.hash ? safeDecode(u.hash.slice(1)) : null);
      } catch {}

      if (!anchor) return;

      evt.preventDefault();

      // same-page click: fromExternal=false (no alignment, no repeated snaps)
      if (!tryScrollToAnchor(anchor, { fromExternal: false })) {
        ensureScroll(anchor, { fromExternal: false });
      }
    });

    // Theme editor / dynamic injection
    document.addEventListener("shopify:section:load", () => {
      const initial2 = getInitialAnchor();
      if (initial2) ensureScroll(initial2, { fromExternal: true, delay: 0 }, 2500);
    });
  });
})();
